import * as v from "valibot";
import { RunSubmissionSchema } from "@palomitas/shared";
import { Game } from "./game/loop";
import type { RunEndReason, RunResult } from "./game/loop";
import { createHud } from "./ui/hud";
import type { Screen } from "./ui/hud";
import { LeaderboardView } from "./ui/leaderboard";

const FORWARDED_PORTAL_QUERY_KEYS = new Set([
  "portal",
  "username",
  "color",
  "speed",
  "ref",
  "avatar_url",
  "team",
  "hp",
  "speed_x",
  "speed_y",
  "speed_z",
  "rotation_x",
  "rotation_y",
  "rotation_z",
]);

sanitizePortalQueryParams();

const canvas = document.getElementById("game") as HTMLCanvasElement | null;
if (!canvas) throw new Error("missing #game canvas");

const hud = createHud();
const leaderboardView = new LeaderboardView();
let dismissHintTimer: number | null = null;

/** Remembers which overlay opened the leaderboard so Back can return there. */
let leaderboardReturnTo: Extract<Screen, "paused" | "gameover"> = "paused";

const game = new Game(canvas, hud, {
  onRunEnd(result) { handleRunEnd(result); },
  onPauseRequest() {
    const screen = hud.getScreen();
    if (screen === "playing") {
      game.pause();
      hud.setScreen("paused");
    } else if (screen === "paused") {
      resumeRun();
    }
  },
  onFirstInteraction() { dismissClickToPlayHint(); },
});

function startRun(): void {
  if (dismissHintTimer !== null) {
    window.clearTimeout(dismissHintTimer);
    dismissHintTimer = null;
  }
  delete document.body.dataset["clickHintDismissing"];
  delete document.body.dataset["clickHintDismissed"];
  hud.setSubmitStatus("idle");
  lastResult = null;
  hud.setScreen("playing");
  game.start();
}

function resumeRun(): void {
  hud.setScreen("playing");
  game.resume();
}

const REASON_TEXT: Record<RunEndReason, string> = {
  starved: "You starved. Should have pecked faster.",
  car: "Oh no. Flattened by a car.",
  abandoned: "",
};

let lastResult: RunResult | null = null;

function handleRunEnd(result: RunResult): void {
  lastResult = result;
  hud.showRunSummary({
    score: result.score,
    durationMs: result.durationMs,
    itemsCollected: result.itemsCollected,
    reason: REASON_TEXT[result.reason],
  });
  hud.setSubmitStatus("idle");
  hud.setScreen("gameover");
}

async function submitRun(): Promise<void> {
  if (!lastResult) return;
  const payload = {
    playerName: hud.getPlayerName(),
    score: lastResult.score,
    durationMs: lastResult.durationMs,
    itemsCollected: lastResult.itemsCollected,
    seed: lastResult.seed,
  };
  const check = v.safeParse(RunSubmissionSchema, payload);
  if (!check.success) {
    hud.setSubmitStatus("error");
    return;
  }
  hud.setSubmitStatus("submitting");
  try {
    const res = await fetch("/api/runs", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(check.output),
    });
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    hud.setSubmitStatus("ok");
    // After a successful submission, open the leaderboard automatically.
    leaderboardReturnTo = "gameover";
    hud.setScreen("leaderboard");
    void leaderboardView.load();
  } catch (err) {
    console.warn("run submission failed", err);
    hud.setSubmitStatus("error");
  }
}

hud.onAction((action) => {
  switch (action) {
    case "resume":
      resumeRun();
      break;
    case "restart":
      game.stop();
      startRun();
      break;
    case "show-leaderboard": {
      const from = hud.getScreen();
      if (from === "paused" || from === "gameover") leaderboardReturnTo = from;
      hud.setScreen("leaderboard");
      void leaderboardView.load();
      break;
    }
    case "refresh-leaderboard":
      void leaderboardView.load();
      break;
    case "close-overlay":
      hud.setScreen(leaderboardReturnTo);
      break;
  }
});

hud.onSubmit(() => { void submitRun(); });

// Drop the player straight in. They'll see the world and a "click to play"
// hint; the click engages pointer lock and the game is fully active.
startRun();

function dismissClickToPlayHint(): void {
  const body = document.body;
  if (body.dataset["clickHintDismissed"] === "true" || body.dataset["clickHintDismissing"] === "true") return;
  body.dataset["clickHintDismissing"] = "true";
  dismissHintTimer = window.setTimeout(() => {
    body.dataset["clickHintDismissed"] = "true";
    delete body.dataset["clickHintDismissing"];
    dismissHintTimer = null;
  }, 260);
}

function sanitizePortalQueryParams(): void {
  const params = new URLSearchParams(window.location.search);
  // Only sanitize when portal semantics are in play.
  if (!params.has("portal") && !params.has("ref")) return;

  const sanitized = new URLSearchParams();
  for (const key of FORWARDED_PORTAL_QUERY_KEYS) {
    const value = params.get(key);
    if (value !== null) sanitized.set(key, value);
  }

  const current = params.toString();
  const next = sanitized.toString();
  if (current === next) return;

  const nextUrl = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", nextUrl);
}
